import { Component, OnInit, inject, viewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ActivatedRoute, Router } from '@angular/router';
import { BehaviorSubject, lastValueFrom } from 'rxjs';
import { Advertiser, Campaign, CampaignAdminRequest, CampaignLocationFilter, CampaignsAdvertiserAdminApiClient, CampaignsCampaignAdminApiClient, CampaignsCampaignLocationFilterAdminApiClient, CampaignsOutboundEventConfigurationApiClient, ConsumerInformationStrategy, StaffInformationStrategy } from 'src/app/api/api-campaigns.service.generated';
import { ProductInfo, RewardsProductApiClient } from 'src/app/api/api-rewards.service.generated';
import { ProductReward } from 'src/app/api/api-campaigns.service.generated';
import { MessageDialogModel } from 'src/app/common/message-dialog/message-dialog.component';
import { ConsumerReferralTabComponent } from '../punch-card-campaign-edit/consumer-referral-tab/consumer-referral-tab.component';
import { StaffReferralTabComponent } from '../punch-card-campaign-edit/staff-referral-tab/staff-referral-tab.component';
import { BannerImage } from '../_/campaign-banner-image/campaign-banner-image.component';
import { CampaignBannerComponent } from '../_/campaign-banner/campaign-banner.component';
import { CampaignBaseEditComponent } from '../_/campaign-base-edit/campaign-base-edit.component';
import { CampaignLocationFiltersComponent } from '../_/campaign-location-filters/campaign-location-filters.component';
import { InformationStrategyComponent } from './information-strategy/information-strategy.component';
import { PERMISSION } from 'src/app/const/permission';
import { HasAnyPermissionDirective } from 'src/app/common/directives/has-any-permission/has-any-permission.directive';
import { PageContainerComponent, BreadcrumbsComponent, ButtonComponent } from 'wecheer-common';
import { MatCardModule } from '@angular/material/card';
import { CampaignMediaComponent } from '../_/campaign-media/campaign-media.component';
import { CampaignOutboundEventsComponent } from '../campaign-outbound-events/campaign-outbound-events.component';
import { MatIconModule } from '@angular/material/icon';
import { AsyncPipe, JsonPipe } from '@angular/common';
import { ScreenService } from 'src/app/common/services/screen.service';
import { AppVersionService } from 'src/app/common/services/app-version.service';
import { FlexLayoutModule } from '@angular/flex-layout';
import { MatTabsModule } from '@angular/material/tabs';
import { CampaignCloneService } from 'src/app/common/services/campaign-clone.service';

@Component({
  selector: 'app-info-campaign-edit',
  templateUrl: './info-campaign-edit.component.html',
  styleUrls: ['./info-campaign-edit.component.scss'],
  imports: [
    CampaignBaseEditComponent,
    HasAnyPermissionDirective,
    MatCardModule,
    MatIconModule,
    MatTabsModule,
    FlexLayoutModule,
    InformationStrategyComponent,
    CampaignBannerComponent,
    CampaignLocationFiltersComponent,
    ConsumerReferralTabComponent,
    StaffReferralTabComponent,
    CampaignMediaComponent,
    CampaignOutboundEventsComponent,
    JsonPipe,
    AsyncPipe,
    PageContainerComponent,
    BreadcrumbsComponent,
    ButtonComponent
  ]
})
export class InfoCampaignEditComponent implements OnInit {
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private dialog = inject(MatDialog);
  private snackBar = inject(MatSnackBar);
  private campaignsApi = inject(CampaignsCampaignAdminApiClient);
  private campaignLocationFiltersApi = inject(CampaignsCampaignLocationFilterAdminApiClient);
  private outboundEventsApi = inject(CampaignsOutboundEventConfigurationApiClient);
  private campaignCloneService = inject(CampaignCloneService);
  private advertiserApi = inject(CampaignsAdvertiserAdminApiClient);
  private rewardProductsApi = inject(RewardsProductApiClient);
  screen = inject(ScreenService);
  appVersion = inject(AppVersionService);


  readonly campaignBase = viewChild<CampaignBaseEditComponent>('campaignBase');
  readonly campaignBanner = viewChild<CampaignBannerComponent>('campaignBanner');
  readonly campaignStrategy = viewChild<InformationStrategyComponent>('campaignStrategy');
  readonly campaignLocationFilters = viewChild<CampaignLocationFiltersComponent>('campaignLocationFilters');
  readonly consumerReferralTab = viewChild<ConsumerReferralTabComponent>('consumerReferralTab');
  readonly staffReferralTab = viewChild<StaffReferralTabComponent>('staffReferralTab');
  readonly campaignOutboundEvents = viewChild<CampaignOutboundEventsComponent>('campaignOutboundEvents');

  editModel: CampaignAdminRequest;

  campaign: Campaign;

  campaignReplay = new BehaviorSubject<Campaign>(undefined);

  campaignId: string;
  advertiserId: string;
  originalCampaignId: string;
  advertiser: Advertiser;
  rewardProducts: ProductReward[];
  readonly permissions = PERMISSION;
  clonedLocationFilters: CampaignLocationFilter;
  clonedConsumerReferralCampaigns: Campaign[];
  clonedStaffReferralCampaigns: Campaign[];

  isLoading: boolean;
  isAdding: boolean;
  isCloning: boolean;

  isContactSync: boolean;

  viewMode: 'scheme' | 'locations' | 'consumer-referral' | 'staff-referral' | 'json' | 'media' | 'banner' | '' = 'scheme';

  isConsumerCampaign: boolean;

  constructor() {
    const route = this.route;

    this.isAdding = route.snapshot.params.campaignId === 'new-s' || route.snapshot.params.campaignId === 'new-c';
    this.isCloning = this.route.snapshot.queryParams.clone === 'true';
    this.campaignId = this.isAdding ? undefined : route.snapshot.params.campaignId;
    this.advertiserId = route.snapshot.params.advertiserId;
    this.originalCampaignId = this.campaignId;

    this.getAdvertiser();
    this.getProductRewards();
  }

  async getProductRewards() {
    this.isLoading = true;
    const productInfos = await lastValueFrom(this.rewardProductsApi.getProductsByAdvertiserId(this.advertiserId, false, false));
    this.rewardProducts = productInfos.map((pi: ProductInfo) =>
      new ProductReward({ productSlug: pi.slug, rewardTitle: pi.name, rewardDescription: pi.description, rewardThumbUrl: pi.photoUrl, rewardPhotoUrl: pi.photoUrl })
    );
    this.isLoading = false;
  }
  async getAdvertiser() {
    this.advertiser = await lastValueFrom(this.advertiserApi.getAdvertiserById(this.advertiserId));
  }

  async ngOnInit() {
    try {
      this.isLoading = true;
      if (this.isCloning) {
        await this.cloneCampaign();
      } else {
        this.campaign = this.campaignId
          ? await lastValueFrom(this.campaignsApi.getCampaign(this.campaignId))
          : new Campaign({
            advertiserId: this.advertiserId,
            strategy:
              this.route.snapshot.params.campaignId === 'new-c'
                ? new ConsumerInformationStrategy({ name: 'ConsumerInformationStrategy' })
                : new StaffInformationStrategy({ name: 'StaffInformationStrategy' })
          });
      }
    } catch (err: any) {
    } finally {
      this.isLoading = false;
    }

    this.isConsumerCampaign = this.campaign.strategy.name === 'ConsumerInformationStrategy';
    // this.viewMode = this.isConsumerCampaign ? 'scheme' : 'locations';  // scheme will be hidden for staff

    // Load location filters - following punch-card pattern
    if (this.isCloning) {
      // When cloning, location filters are already loaded in cloneCampaign()
      this.campaignLocationFilters().setFormValue(this.clonedLocationFilters);
    } else {
      const clf = this.campaignId
        ? await lastValueFrom(this.campaignLocationFiltersApi.getByCampaignId(this.campaignId))
        : new CampaignLocationFilter({ campaignId: this.campaignId, filters: [] });
      this.campaignLocationFilters().setFormValue(clf);
    }

    this.campaignReplay.next(this.campaign);

    setTimeout(() => {
      this.syncBanners();

      // Set cloned referral campaigns after components are ready
      if (this.isCloning && this.isConsumerCampaign) {
        if (this.clonedConsumerReferralCampaigns && this.consumerReferralTab()) {
          this.consumerReferralTab().setFormValue(this.clonedConsumerReferralCampaigns);
        }
        if (this.clonedStaffReferralCampaigns && this.staffReferralTab()) {
          this.staffReferralTab().setFormValue(this.clonedStaffReferralCampaigns);
        }
      }
    }, 500);
  }

  syncBanners() {
    // sync baseCampaign main banner with banner slider tab (can be edited from 2 places)
    this.campaignBase().mainBannerChanged.subscribe(banner => { this.campaignBanner().setMainBanner(banner); });
    this.campaignBanner().mainBannerChanged.subscribe(banner => { this.campaignBase().setMainBanner(banner); });

    this.campaignBanner().setBanners(this.campaign.strategy.banner);

    if (!this.campaign.strategy.banner) {
      // old campaings have banner set on campaign.photoBannerUrl prop -> add it to the banner list
      if (this.campaign.photoBannerUrl)
        this.campaignBanner().setMainBanner(new BannerImage({ photoUrl: this.campaign.photoBannerUrl }));
    }
    else {
      // new campaign will have banner list -> set it also on the campaign base main banner
      const strategyBanner = this.campaign.strategy.banner;
      if (strategyBanner && strategyBanner.url1)
        this.campaignBase().setMainBanner(new BannerImage({ photoUrl: strategyBanner.url1 }))
    }
  }


  private async cloneCampaign() {
    const { campaign, locationFilters } = await this.campaignCloneService.cloneCampaign(this.originalCampaignId, this.advertiserId);

    this.campaign = campaign;
    this.isAdding = true;
    this.campaignId = undefined;

    // Store location filters to be set in setTimeout when viewChild is ready
    this.clonedLocationFilters = locationFilters;

    const [consumerReferralCampaigns, staffReferralCampaigns] = await Promise.all([
      lastValueFrom(this.campaignsApi.getConsumerReferralCampaigns(this.originalCampaignId)),
      lastValueFrom(this.campaignsApi.getStaffReferralCampaigns(this.originalCampaignId))
    ]);

    const clonedConsumerReferralCampaigns = consumerReferralCampaigns?.map(campaign => {
      const clonedCampaign = new Campaign({ ...campaign, id: undefined, campaignId: undefined });

      if (clonedCampaign.strategy && 'refCampaignId' in clonedCampaign.strategy) {
        clonedCampaign.strategy.refCampaignId = undefined;
      }
      return clonedCampaign;
    });

    const clonedStaffReferralCampaigns = staffReferralCampaigns?.map(campaign => {
      const clonedCampaign = new Campaign({ ...campaign, id: undefined, campaignId: undefined });

      if (clonedCampaign.strategy && 'refCampaignId' in clonedCampaign.strategy) {
        clonedCampaign.strategy.refCampaignId = undefined;
      }
      return clonedCampaign;
    });

    // Store cloned referral campaigns to be set later when components are ready
    this.clonedConsumerReferralCampaigns = clonedConsumerReferralCampaigns;
    this.clonedStaffReferralCampaigns = clonedStaffReferralCampaigns;
  }

  forceRefresh() {
    const c = this.campaign;
    this.campaign = null;
    this.isLoading = true;
    setTimeout(() => {
      this.campaign = c;
      this.isLoading = false;
    }, 500);
  }


  async onSave() {
    this.campaignOutboundEvents()?.markAsTouched();

    const request = this.campaignBase().getFormValue();
    if (!request) return;

    let strategy = this.campaignStrategy().getFormValue();
    if (!strategy) return;

    if (!this.campaignOutboundEvents().isValid()) return;

    request.campaignId = this.campaignId;
    request.advertiserId = this.advertiserId;
    request.strategy = strategy;
    request.campaignLocationFilter = this.campaignLocationFilters().getFormValue();
    request.strategy.banner = this.campaignBanner().getBanners();

    if (this.isConsumerCampaign) {
      // attach consumer referral campaigns to the request
      request.consumerReferralCampaigns = this.consumerReferralTab().getFormConsumerReferralCampaigns();

      // attach staff referral campaigns to the request
      request.staffReferralCampaigns = this.staffReferralTab().getFormStaffReferralCampaigns();
    }

    this.isLoading = true;
    try {
      const savedCampaign = await lastValueFrom(this.campaignsApi.createCampaign(request));

      const outboundEventsConfig = this.campaignOutboundEvents()?.getFormValue();
      if (outboundEventsConfig && savedCampaign?.campaignId) {
        try {
          await lastValueFrom(this.outboundEventsApi.updateCampaignConfig(savedCampaign.campaignId, outboundEventsConfig));
        } catch (err) {
          console.warn('Failed to save outbound events configuration:', err);
        }
      }

      this.snackBar.open(`Campaign ${request.title} was saved`, null, { duration: 3000 });
      this.router.navigate(['advertisers', this.advertiserId]);

    } catch (err: any) {
      if (err.status === 400 && err.response)
        new MessageDialogModel(err.response).show(this.dialog);
    } finally {
      this.isLoading = false;
    }
  }

  get title() {
    return `${this.isAdding ? 'Add' : 'Edit'} ${this.isConsumerCampaign ? 'Consumer' : 'Staff'} Information Campaign`;
  }

}
